/**
 * Workarounds for features SheetJS Community Edition doesn't support.
 *
 * SheetJS CE writes cell values but silently drops cell styles (`cell.s`) and
 * other Pro-only features. An .xlsx is just a zip of XML, so the patches here
 * take the bytes SheetJS produces (`write(wb, { type: 'array' })`), open the
 * zip, edit the XML directly, and re-zip. The zip is re-emitted with STORE
 * entries, which is what SheetJS does by default anyway.
 *
 * No dependencies: zip parsing/writing is done here, and DEFLATE entries (if a
 * caller passed `compression: true`) are inflated with the browser's native
 * DecompressionStream.
 *
 * Current patches:
 *   - styleXlsxCells: bold and/or center-align selected cells
 *   - downloadXlsx:   trigger a browser download of the patched bytes
 */

interface ZipEntry {
  name: string;
  data: Uint8Array;
}

const SIG_LOCAL = 0x04_03_4B_50;
const SIG_CENTRAL = 0x02_01_4B_50;
const SIG_EOCD = 0x06_05_4B_50;

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xED_B8_83_20 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let crc = 0xFF_FF_FF_FF;
  for (const byte of bytes) {
    crc = CRC_TABLE[(crc ^ byte) & 0xFF] ^ (crc >>> 8);
  }
  return (crc ^ 0xFF_FF_FF_FF) >>> 0;
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function readZip(bytes: Uint8Array): Promise<ZipEntry[]> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const decoder = new TextDecoder();

  // Locate End Of Central Directory (scan backwards; comment may follow it).
  let eocd = -1;
  for (let i = bytes.length - 22; i >= 0; i--) {
    if (view.getUint32(i, true) === SIG_EOCD) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0)
    throw new Error('Not a zip file (EOCD not found)');

  const entryCount = view.getUint16(eocd + 10, true);
  let offset = view.getUint32(eocd + 16, true);

  const entries: ZipEntry[] = [];
  for (let i = 0; i < entryCount; i++) {
    if (view.getUint32(offset, true) !== SIG_CENTRAL)
      throw new Error('Bad central directory entry');
    const method = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const nameLen = view.getUint16(offset + 28, true);
    const extraLen = view.getUint16(offset + 30, true);
    const commentLen = view.getUint16(offset + 32, true);
    const localOffset = view.getUint32(offset + 42, true);
    const name = decoder.decode(bytes.subarray(offset + 46, offset + 46 + nameLen));

    if (view.getUint32(localOffset, true) !== SIG_LOCAL)
      throw new Error('Bad local header');
    const localNameLen = view.getUint16(localOffset + 26, true);
    const localExtraLen = view.getUint16(localOffset + 28, true);
    const dataStart = localOffset + 30 + localNameLen + localExtraLen;
    const raw = bytes.subarray(dataStart, dataStart + compressedSize);

    let data: Uint8Array;
    if (method === 0)
      data = raw;
    else if (method === 8)
      data = await inflateRaw(raw);
    else throw new Error(`Unsupported zip compression method ${method}`);

    entries.push({ name, data });
    offset += 46 + nameLen + extraLen + commentLen;
  }
  return entries;
}

function writeZip(entries: ZipEntry[]): Uint8Array {
  const encoder = new TextEncoder();
  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  let offset = 0;

  for (const entry of entries) {
    const nameBytes = encoder.encode(entry.name);
    const crc = crc32(entry.data);
    const size = entry.data.length;

    const local = new Uint8Array(30 + nameBytes.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, SIG_LOCAL, true);
    lv.setUint16(4, 20, true); // version needed
    lv.setUint16(6, 0, true); // flags
    lv.setUint16(8, 0, true); // method: STORE
    lv.setUint16(10, 0, true); // mod time
    lv.setUint16(12, 0x21, true); // mod date (1980-01-01)
    lv.setUint32(14, crc, true);
    lv.setUint32(18, size, true);
    lv.setUint32(22, size, true);
    lv.setUint16(26, nameBytes.length, true);
    lv.setUint16(28, 0, true);
    local.set(nameBytes, 30);

    const central = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, SIG_CENTRAL, true);
    cv.setUint16(4, 20, true); // version made by
    cv.setUint16(6, 20, true); // version needed
    cv.setUint16(8, 0, true);
    cv.setUint16(10, 0, true); // method: STORE
    cv.setUint16(12, 0, true);
    cv.setUint16(14, 0x21, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, size, true);
    cv.setUint32(24, size, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint16(30, 0, true); // extra len
    cv.setUint16(32, 0, true); // comment len
    cv.setUint16(34, 0, true); // disk
    cv.setUint16(36, 0, true); // internal attrs
    cv.setUint32(38, 0, true); // external attrs
    cv.setUint32(42, offset, true); // local header offset
    central.set(nameBytes, 46);

    localParts.push(local, entry.data);
    centralParts.push(central);
    offset += local.length + size;
  }

  const centralSize = centralParts.reduce((n, p) => n + p.length, 0);
  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  ev.setUint32(0, SIG_EOCD, true);
  ev.setUint16(4, 0, true);
  ev.setUint16(6, 0, true);
  ev.setUint16(8, entries.length, true);
  ev.setUint16(10, entries.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true);
  ev.setUint16(20, 0, true);

  const out = new Uint8Array(offset + centralSize + 22);
  let pos = 0;
  for (const part of [...localParts, ...centralParts, eocd]) {
    out.set(part, pos);
    pos += part.length;
  }
  return out;
}

export interface CellStyle {
  bold?: boolean;
  center?: boolean;
}

/** Appends one child to a `<name count="N">...</name>` block, returning the new child's index. */
function appendToCountedList(xml: string, listName: string, child: string): { xml: string; index: number } {
  const match = new RegExp(`<${listName} count="(\\d+)">`).exec(xml);
  if (!match)
    throw new Error(`styles.xml has no <${listName}>`);
  const index = Number(match[1]);
  const next = xml
    .replace(match[0], `<${listName} count="${index + 1}">`)
    .replace(`</${listName}>`, `${child}</${listName}>`);
  return { xml: next, index };
}

/** Clones the default font as bold and returns its fontId. */
function addBoldFont(stylesXml: string): { xml: string; fontId: number } {
  const match = /<fonts count="\d+">(<font>.*?<\/font>)/.exec(stylesXml);
  if (!match)
    throw new Error('styles.xml has no default <font>');
  const boldFont = match[1].replace('<font>', '<font><b/>');
  const { xml, index } = appendToCountedList(stylesXml, 'fonts', boldFont);
  return { xml, fontId: index };
}

/**
 * Registers one cellXfs entry per distinct style combination and returns a
 * lookup from style key to cellXfs index.
 */
function addStyleXfs(stylesXml: string, styles: CellStyle[]): { xml: string; indexByKey: Map<string, number> } {
  let xml = stylesXml;
  let boldFontId: number | undefined;
  const indexByKey = new Map<string, number>();

  for (const style of styles) {
    const key = styleKey(style);
    if (indexByKey.has(key))
      continue;

    let fontId = 0;
    if (style.bold) {
      if (boldFontId === undefined)
        ({ xml, fontId: boldFontId } = addBoldFont(xml));
      fontId = boldFontId;
    }

    const attrs = [
      'numFmtId="0"',
      `fontId="${fontId}"`,
      'fillId="0"',
      'borderId="0"',
      'xfId="0"',
      style.bold ? 'applyFont="1"' : '',
      style.center ? 'applyAlignment="1"' : '',
    ].filter(Boolean).join(' ');
    const xf = style.center
      ? `<xf ${attrs}><alignment horizontal="center"/></xf>`
      : `<xf ${attrs}/>`;

    const added = appendToCountedList(xml, 'cellXfs', xf);
    xml = added.xml;
    indexByKey.set(key, added.index);
  }

  return { xml, indexByKey };
}

function styleKey(style: CellStyle): string {
  return `${style.bold ? 'b' : ''}${style.center ? 'c' : ''}`;
}

function columnLetterToIndex(letters: string): number {
  let n = 0;
  for (const ch of letters) n = n * 26 + ((ch.codePointAt(0) ?? 0) - 64);
  return n - 1;
}

const CELL_RE = /<c r="([A-Z]+)(\d+)"([^>]*?)(\/?)>/g;

/** Points every <c> with a style at the matching cellXfs index. */
function applyStylesToCells(
  sheetXml: string,
  cellStyle: (col: number, row: number) => CellStyle | undefined,
  indexByKey: Map<string, number>,
): string {
  return sheetXml.replaceAll(CELL_RE, (full, colLetters: string, rowStr: string, attrs: string, selfClose: string) => {
    const style = cellStyle(columnLetterToIndex(colLetters), Number(rowStr) - 1);
    const index = style && indexByKey.get(styleKey(style));
    if (index === undefined)
      return full;
    const rest = attrs.replace(/\s*s="\d+"/, '');
    return `<c r="${colLetters}${rowStr}" s="${index}"${rest}${selfClose}>`;
  });
}

export interface StyleCellsOptions {
  /** Return the style for a cell (0-based col/row), or undefined to leave it alone. */
  cellStyle: (col: number, row: number) => CellStyle | undefined;
}

/**
 * Takes raw .xlsx bytes (e.g. `write(wb, { type: 'array', bookType: 'xlsx' })`)
 * and returns new bytes with the selected cells bolded and/or center-aligned.
 */
export async function styleXlsxCells(xlsxBytes: Uint8Array, options: StyleCellsOptions): Promise<Uint8Array> {
  const entries = await readZip(xlsxBytes);
  const decoder = new TextDecoder();
  const encoder = new TextEncoder();

  const stylesEntry = entries.find((e) => e.name === 'xl/styles.xml');
  if (!stylesEntry)
    throw new Error('Workbook has no xl/styles.xml');

  const sheets = entries
    .filter((e) => /^xl\/worksheets\/sheet\d+\.xml$/.test(e.name))
    .map((entry) => ({ entry, xml: decoder.decode(entry.data) }));

  // Pass 1: find which style combinations are actually used.
  const usedStyles = new Map<string, CellStyle>();
  for (const { xml } of sheets) {
    for (const [, colLetters, rowStr] of xml.matchAll(CELL_RE)) {
      const style = options.cellStyle(columnLetterToIndex(colLetters), Number(rowStr) - 1);
      if (style && (style.bold || style.center))
        usedStyles.set(styleKey(style), style);
    }
  }

  // Pass 2: register them in styles.xml and stamp the cells.
  const { xml: stylesXml, indexByKey } = addStyleXfs(decoder.decode(stylesEntry.data), [...usedStyles.values()]);
  stylesEntry.data = encoder.encode(stylesXml);
  for (const { entry, xml } of sheets) {
    entry.data = encoder.encode(applyStylesToCells(xml, options.cellStyle, indexByKey));
  }

  return writeZip(entries);
}

/** Browser download helper for the patched bytes. */
export function downloadXlsx(bytes: Uint8Array, filename: string): void {
  const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
